import {
  CampaignsAndTeasersByMarketQueryResult,
  GetCampaignsAndTeasersByMarketResult,
  Link,
  SanityClientFetchOptions,
  TripsCollection,
} from '../../data/sanity/types';
import { SanityFetcherService } from '../../services/SanityService/SanityFetcher';
import { SemboMarket } from '../../utils/markets';
import { getRedirectForCampaignToPlacePagesEx } from '../../utils/middleware.utils';
import { findYoutubeThumbnail } from '../../utils/urlHelpers';
import {
  aiSuggestionsByMarket,
  campaignsAndTeasersByMarket,
  firstMarketTripCollection,
  linksByMarket,
  mainBannerByMarket,
  marketImageByMarketHost,
  secondaryCampaignByMarket,
  tagsByMarket,
} from './MarketPage.queries';
import {
  GetMainBannerByMarketResult,
  GetMarketImageByMarketHostResult,
  GetSecondaryCampaignByMarketResult,
  GetTagsByMarketResult,
  MainBannerByMarketQueryResult,
  MarketImageByMarketHostQueryResult,
  SecondaryCampaignByMarketQueryResult,
  TagsByMarketQueryResult,
} from './MarketPage.types';

export const getLinksByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<Link[]> => {
  const queryUrl = linksByMarket(market);
  const result = await SanityFetcherService.fetchAndLog<Link[] | null>(
    queryUrl,
    previewSettings
  );
  return result ?? [];
};

export const getCampaignsAndTeasersByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetCampaignsAndTeasersByMarketResult> => {
  const result =
    await SanityFetcherService.fetchAndLog<CampaignsAndTeasersByMarketQueryResult>(
      campaignsAndTeasersByMarket(market),
      previewSettings
    );

  return result ?? [];
};

export const getMarketImageByMarketHost = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions,
  mediaName: 'image' | 'expressiveImage' = 'image'
): Promise<GetMarketImageByMarketHostResult | null> => {
  const result =
    await SanityFetcherService.fetchAndLog<MarketImageByMarketHostQueryResult>(
      marketImageByMarketHost(market, mediaName),
      previewSettings
    );

  if (!result) {
    return null;
  }

  return {
    ...result,
    focalPointX: result?.focalPointX ?? 0.5,
    focalPointY: result?.focalPointY ?? 0.5,
  };
};

export const getMainBannerByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetMainBannerByMarketResult> => {
  const result =
    await SanityFetcherService.fetchAndLog<MainBannerByMarketQueryResult>(
      mainBannerByMarket(market),
      previewSettings
    );

  if (
    result?.media &&
    '_type' in result.media &&
    result.media._type === 'video'
  ) {
    return {
      ...result,
      thumbnail: await findYoutubeThumbnail(result.media.url),
    };
  }

  if (result) {
    result.placeInfo = await getRedirectForCampaignToPlacePagesEx(
      market,
      result.slug
    );
  }

  return result;
};

export const getSecondaryCampaignByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetSecondaryCampaignByMarketResult> => {
  const result =
    await SanityFetcherService.fetchAndLog<SecondaryCampaignByMarketQueryResult>(
      secondaryCampaignByMarket(market),
      previewSettings
    );

  return result;
};

export const getAiSuggestionsByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<string[]> => {
  const result = await SanityFetcherService.fetchAndLog<string[]>(
    aiSuggestionsByMarket(market),
    previewSettings
  );

  return result;
};

export const getFirstMarketTripCollection = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<TripsCollection | null> => {
  const result = await SanityFetcherService.fetchAndLog<TripsCollection | null>(
    firstMarketTripCollection(market),
    previewSettings
  );

  return result;
};

export const getTagsByMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetTagsByMarketResult> => {
  return SanityFetcherService.fetchAndLog<TagsByMarketQueryResult>(
    tagsByMarket(market),
    previewSettings
  );
};
